/**
 * Allowlist sanitizer for the app's own rich notes (bold/italic/underline,
 * lists, paragraphs). Everything else — tags, attributes, URLs — is dropped.
 */
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'P', 'DIV', 'BR', 'SPAN']);

export function sanitizeNotesHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Element | Document | DocumentFragment) => {
    for (const el of Array.from(node.querySelectorAll('*'))) {
      if (!ALLOWED_TAGS.has(el.tagName)) {
        // unwrap: keep the text/children, drop the tag
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) parent.insertBefore(el.firstChild, el);
          parent.removeChild(el);
        }
        continue;
      }
      for (const attr of Array.from(el.attributes)) el.removeAttribute(attr.name);
    }
  };
  walk(doc.body as unknown as Element);
  return doc.body.innerHTML;
}

/** True when the stored notes carry any real text. */
export function notesHaveContent(html: string | null | undefined): boolean {
  if (!html) return false;
  return htmlToText(html).trim().length > 0;
}

/**
 * The cards chosen at the BSP live as free text in the premier-entretien form
 * ("Card name: justification" per line/bullet, old-deck wordings included).
 * Split them into {name, note} items so the référente sees discrete cards, not
 * a wall of prose. Fewer than 2 parsed lines → the caller keeps a plain quote.
 */
export function parseBspCards(html: string): { name: string; note: string | null }[] {
  return htmlToText(html)
    .split('\n')
    .map((l) => l.replace(/^[-•–—\s]+/, '').trim())
    .filter((l) => l.length > 1)
    .map((l) => {
      // split at the first ":" that isn't part of a time like "13:30"
      const m = l.match(/^(.{2,80}?)\s*:\s+(.*)$/);
      return m ? { name: m[1].trim(), note: m[2].trim() || null } : { name: l, note: null };
    });
}

/** V2 free-text fields come from a rich-text editor — flatten to plain text. */
export function htmlToText(html: string): string {
  const withBreaks = html
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/(p|li|ul|ol|div|h[1-6]|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '')
    .replace(/ /g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
